import { ApiError } from "@risalabs_frontend_org/api-client";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";
import type {
  PdfCompressionRequest,
  PdfCompressionResponse,
} from "./pdfCompressionTypes";

/** Authenticated multipart PDF compression requests. */
export class PdfCompressionService {
  /**
   * Send an uploaded PDF or storage path as multipart form data.
   * Returns size metadata and base64 PDF bytes without persisting the output.
   * The optional signal cancels the client request, not backend processing.
   */
  static async compress(
    payload: PdfCompressionRequest,
    signal?: AbortSignal,
  ): Promise<PdfCompressionResponse> {
    const token = await getBearerToken();
    if (!token) throw new Error("Failed to retrieve bearer token");

    const form = new FormData();
    if (payload.file !== undefined) {
      form.append("file", payload.file);
    } else if (payload.gcs_path !== undefined) {
      form.append("gcs_path", payload.gcs_path);
    }
    if (payload.preset !== undefined) {
      form.append("preset", payload.preset);
    }
    if (payload.target_bytes !== undefined) {
      form.append("target_bytes", String(payload.target_bytes));
    }
    if (payload.target_compression !== undefined) {
      form.append("target_compression", String(payload.target_compression));
    }

    // Leave Content-Type unset so the browser supplies the multipart boundary.
    const response = await fetch(API_ENDPOINTS.COMPRESS_PDF, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
      signal,
    });
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new ApiError(response.status, response.statusText, errorBody);
    }
    return response.json() as Promise<PdfCompressionResponse>;
  }
}
